import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, RefreshCw, AlertTriangle, FileCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABELS = {
  business_name: 'Business Name (as BIR registered)',
  tin: 'TIN',
  atp_number: 'ATP Number',
  machine_serial: 'Machine Serial No. (MSN)',
  bir_permit_number: 'BIR Permit Number',
  vat_type: 'VAT Type',
};

function Field({ label, declared, verified, onChange, changed }) {
  return (
    <div className={`rounded-lg border p-3 ${changed ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] text-slate-400 mb-1">Declared by company</p>
          <p className="text-sm font-mono text-slate-700 bg-slate-50 rounded px-2 py-1">{declared || '—'}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 mb-1">
            Verified value {changed && <span className="text-amber-600 font-semibold">(edited)</span>}
          </p>
          <input
            className={`w-full text-sm font-mono rounded border px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 ${
              changed ? 'border-amber-400 bg-amber-50' : 'border-slate-200'
            }`}
            value={verified}
            onChange={e => onChange(e.target.value)}
          />
        </div>
      </div>
      {changed && (
        <p className="text-[10px] text-amber-700 mt-1.5">
          Pre-filled data and submitted document data are not the same — edited to match documents.
        </p>
      )}
    </div>
  );
}

export default function BIRReviewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState({});
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showReject, setShowReject] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/bir/review/${token}`)
      .then(r => {
        const req = r.data;
        setRequest(req);
        setVerified({
          verified_business_name: req.declared_business_name || '',
          verified_tin: req.declared_tin || '',
          verified_atp_number: req.declared_atp_number || '',
          verified_machine_serial: req.declared_machine_serial || '',
          verified_bir_permit_number: req.declared_bir_permit_number || '',
          verified_vat_type: req.declared_vat_type || 'non_vat',
        });
      })
      .catch(e => setError(e.response?.data?.detail || 'Invalid or expired link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/bir/review/${token}/action`, {
        action,
        ...verified,
        verified_receipt_workflow: request?.declared_receipt_workflow || 'staged',
        senior_citizen_discount_enabled: request?.senior_citizen_discount_enabled || false,
        reviewer_notes: reviewerNotes,
        rejection_reason: rejectionReason,
      });
      setResult({ action, ...res.data });
    } catch (e) {
      setError(e.response?.data?.detail || 'Action failed. Please try again.');
    }
    setSubmitting(false);
  };

  const isChanged = (declaredKey, verifiedKey) => {
    const d = (request?.[declaredKey] || '').toLowerCase().trim();
    const v = (verified[verifiedKey] || '').toLowerCase().trim();
    return d && v && d !== v;
  };

  const totalDiscrepancies = [
    isChanged('declared_business_name', 'verified_business_name'),
    isChanged('declared_tin', 'verified_tin'),
    isChanged('declared_atp_number', 'verified_atp_number'),
    isChanged('declared_machine_serial', 'verified_machine_serial'),
    isChanged('declared_bir_permit_number', 'verified_bir_permit_number'),
    isChanged('declared_vat_type', 'verified_vat_type'),
  ].filter(Boolean).length;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <RefreshCw size={24} className="animate-spin text-slate-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-red-200 p-8 text-center shadow-sm">
        <XCircle size={40} className="mx-auto mb-3 text-red-500" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Link Unavailable</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  if (result) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className={`max-w-md w-full rounded-2xl border p-8 text-center shadow-sm ${
        result.action === 'approve' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
      }`}>
        {result.action === 'approve'
          ? <CheckCircle size={40} className="mx-auto mb-3 text-emerald-500" />
          : <XCircle size={40} className="mx-auto mb-3 text-red-500" />
        }
        <h2 className="text-lg font-bold text-slate-800 mb-2">
          {result.action === 'approve' ? 'BIR Activated' : 'Application Rejected'}
        </h2>
        <p className="text-sm text-slate-600">{result.message}</p>
        {result.has_discrepancies && result.discrepancies?.length > 0 && (
          <div className="mt-4 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-800 mb-2">{result.discrepancies.length} field(s) corrected:</p>
            {result.discrepancies.map(d => (
              <div key={d.field} className="text-xs text-amber-700 mb-1">
                <span className="font-medium capitalize">{d.field.replace(/_/g, ' ')}:</span> "{d.declared}" → "{d.verified}"
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-[#1A4D2E] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FileCheck size={15} /> BIR Activation Review
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Review BIR Application</h1>
          <p className="text-sm text-slate-500 mt-1">Verify the submitted data against the company's documents. Edit if anything differs.</p>
        </div>

        {/* Company info */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-slate-400">Company</p><p className="font-semibold">{request.org_name}</p></div>
            <div><p className="text-xs text-slate-400">Branch</p><p className="font-semibold">{request.branch_name}</p></div>
            <div><p className="text-xs text-slate-400">Submitted by</p><p>{request.submitted_by_email}</p></div>
            <div><p className="text-xs text-slate-400">Submitted at</p><p>{new Date(request.submitted_at).toLocaleString()}</p></div>
          </div>
          {request.applicant_notes && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">Applicant notes</p>
              <p className="text-sm text-slate-600 italic">{request.applicant_notes}</p>
            </div>
          )}
        </div>

        {/* Discrepancy warning */}
        {totalDiscrepancies > 0 && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle size={15} className="shrink-0" />
            {totalDiscrepancies} field(s) differ from declared values. Review and confirm the corrected data before approving.
          </div>
        )}

        {/* Fields */}
        <div className="space-y-3">
          <Field label={FIELD_LABELS.business_name}
            declared={request.declared_business_name}
            verified={verified.verified_business_name}
            onChange={v => setVerified(p => ({ ...p, verified_business_name: v }))}
            changed={isChanged('declared_business_name', 'verified_business_name')} />
          <Field label={FIELD_LABELS.tin}
            declared={request.declared_tin}
            verified={verified.verified_tin}
            onChange={v => setVerified(p => ({ ...p, verified_tin: v }))}
            changed={isChanged('declared_tin', 'verified_tin')} />
          <Field label={FIELD_LABELS.atp_number}
            declared={request.declared_atp_number}
            verified={verified.verified_atp_number}
            onChange={v => setVerified(p => ({ ...p, verified_atp_number: v }))}
            changed={isChanged('declared_atp_number', 'verified_atp_number')} />
          <Field label={FIELD_LABELS.machine_serial}
            declared={request.declared_machine_serial}
            verified={verified.verified_machine_serial}
            onChange={v => setVerified(p => ({ ...p, verified_machine_serial: v }))}
            changed={isChanged('declared_machine_serial', 'verified_machine_serial')} />
          <Field label={FIELD_LABELS.bir_permit_number}
            declared={request.declared_bir_permit_number}
            verified={verified.verified_bir_permit_number}
            onChange={v => setVerified(p => ({ ...p, verified_bir_permit_number: v }))}
            changed={isChanged('declared_bir_permit_number', 'verified_bir_permit_number')} />

          {/* VAT type */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">VAT Type</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-slate-400 mb-1">Declared</p>
                <p className="text-sm bg-slate-50 rounded px-2 py-1">{request.declared_vat_type === 'vat_registered' ? 'VAT Registered (12%)' : 'Non-VAT'}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 mb-1">Verified</p>
                <select
                  className="w-full text-sm border border-slate-200 rounded px-2 py-1 bg-white"
                  value={verified.verified_vat_type}
                  onChange={e => setVerified(p => ({ ...p, verified_vat_type: e.target.value }))}
                >
                  <option value="non_vat">Non-VAT Registered</option>
                  <option value="vat_registered">VAT Registered (12%)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Reviewer notes */}
        <div>
          <label className="text-xs text-slate-500 font-medium">Reviewer Notes (optional)</label>
          <textarea
            className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            rows={2}
            placeholder="Internal notes about this review..."
            value={reviewerNotes}
            onChange={e => setReviewerNotes(e.target.value)}
          />
        </div>

        {/* Action buttons */}
        {!showReject ? (
          <div className="flex gap-3">
            <button
              onClick={() => setShowReject(true)}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl border border-red-200 text-red-600 font-semibold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
            >
              <XCircle size={16} /> Reject
            </button>
            <button
              data-testid="approve-bir-btn"
              onClick={() => handleAction('approve')}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-[#1A4D2E] text-white font-semibold hover:bg-[#14532d] transition-colors flex items-center justify-center gap-2"
            >
              {submitting
                ? <><RefreshCw size={15} className="animate-spin" /> Processing…</>
                : <><CheckCircle size={15} /> {totalDiscrepancies > 0 ? `Approve with ${totalDiscrepancies} correction(s)` : 'Approve & Activate'}</>
              }
            </button>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-semibold text-red-800">Reason for rejection</p>
            <textarea
              className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              rows={2}
              placeholder="Tell the company what needs to be corrected..."
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
            />
            <div className="flex gap-2">
              <button onClick={() => setShowReject(false)} className="flex-1 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50">
                Cancel
              </button>
              <button
                data-testid="confirm-reject-btn"
                onClick={() => handleAction('reject')}
                disabled={submitting || !rejectionReason.trim()}
                className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? 'Rejecting…' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
